import React, { useEffect, useState } from 'react';
import CryptoJS from 'crypto-js';
import sha256 from 'crypto-js/sha256';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import { validateEmail, validatePassword } from 'utils/commonHelpers';

const RegisterDialog = ({ onClose }) => {
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [lengthLimits, setLengthLimits] = useState({});
    const [isContainUpper, setIsContainUpper] = useState(true);
    const [isContainLower, setIsContainLower] = useState(true);
    const [isContainNumber, setIsContainNumber] = useState(true);
    const [spclChar, setSpclChar] = useState('!@#$%^&*()_+-={}[]|\\:;<>.?/');

    useEffect(() => {}, []);

    const handleSelectedFile = (e) => {
        if (e.target.files[0].name.split('.')[1] !== 'xml') {
            toast.warning('The organization key file should be xml file');
            return true;
        }
        setSelectedFile(e.target.files[0]);
        setSelectedFileName(e.target.files[0].name);
    };

    const getSecurityParams = async () => {
        const headers = {
            'Content-Type': 'application/json'
        };

        try {
            const res = await ApiHelper.post('/api/auth/security_settings/', {}, headers, {}, false);

            if (res.data && res.data.passwordSettings) {
                setLengthLimits({
                    usernameMaxLength: res.data.passwordSettings.usernameMaxLength || 120,
                    passwordMaxLength: res.data.passwordSettings.passwordMaxLength || 120
                });
                return true;
            } else {
                return false;
            }
        } catch (err) {
            toast.error('Failed to fetch security settings:', err);
            return false;
        }
    };

    const handleSubmit = async () => {
        const isSecurityValid = await getSecurityParams();

        if (!isSecurityValid) {
            return;
        }

        if (username.trim() === '') {
            toast.warning('Username is required');
            return;
        }
        if (!validateEmail(email)) {
            toast.warning('Invalid email format');
            return;
        }
        if (password === '') {
            toast.warning('Password is required');
            return;
        }
        const result = validatePassword(
            password,
            lengthLimits.passwordMinLength,
            lengthLimits.passwordMaxLength,
            isContainUpper,
            isContainLower,
            isContainNumber,
            spclChar
        );
        if (!result.isValid) {
            result.errors.forEach((error) => toast.warning(error));
            return;
        }
        if (password !== confirmPassword) {
            toast.warning('Confirm password does not match');
            return;
        }
        if (!selectedFile) {
            toast.warning('Please upload the Organization Key file (.xml)');
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append('username', username);
        formData.append('email', email);
        formData.append('password', sha256(password).toString(CryptoJS.enc.Hex));
        formData.append('file', selectedFile);

        ApiHelper.post(
            '/api/auth/register/',
            formData,
            {
                headers: {
                    'content-type': 'multipart/form-data'
                }
            },
            {},
            false
        )
            .then((res) => {
                const msg = (res && res.data && res.data.message) || 'Organization registered. Please check your email.';
                toast.success(msg);
                setLoading(false);
                onClose();
            })
            .catch((err) => {
                const backendMsg = err && err.response && err.response.data && err.response.data.message;
                toast.error(backendMsg ? `Failed: ${backendMsg}` : 'Failed to register');
                setLoading(false);
            });
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Please Register Now!</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 2, mt: 2 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Username"
                        value={username}
                        // inputProps={{
                        //     maxLength: lengthLimits.usernameMaxLength
                        // }}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 2, mt: 2 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 2, mt: 2 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        type="password"
                        label="Password"
                        value={password}
                        inputProps={{
                            maxLength: lengthLimits.passwordMaxLength
                        }}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 2, mt: 2 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        type="password"
                        label="Confirm Password"
                        value={confirmPassword}
                        inputProps={{
                            maxLength: lengthLimits.passwordMaxLength
                        }}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 1, mt: 2, display: 'flex' }}>
                    <Button variant="contained" component="label">
                        Upload Organization Key File
                        <input type="file" accept=".xml,.txt" hidden onChange={handleSelectedFile} />
                    </Button>
                    <Typography sx={{ mt: 1, marginLeft: '20px' }}>{selectedFileName}</Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <LoadingButton onClick={handleSubmit} loading={loading} color="primary" variant="contained">
                    <span>Submit</span>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

RegisterDialog.propTypes = {
    onClose: PropTypes.func
};

export default RegisterDialog;
